import { useCallback, useEffect, useState } from 'react';
import {
  PROFILE_STORES_REVIEW_REPLY,
  PROFILE_STORES_REVIEWS,
} from '../../../shared/constants/endpoints';

export const REVIEW_REPLY_MAX_LENGTH = 1000;

const buildReviewsQuery = ({ page = 0, size = 20, serviceUuid, rating, sort = 'newest' }) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  if (serviceUuid) params.set('serviceUuid', serviceUuid);
  if (rating) params.set('rating', String(rating));
  if (sort) params.set('sort', sort);
  return `${PROFILE_STORES_REVIEWS}?${params.toString()}`;
};

// Fetches the merchant "Customer Reviews" list (summary + paginated, service-linked reviews).
const useStoreReviews = ({ request, filters = {} }) => {
  const { page = 0, size = 20, serviceUuid = null, rating = null, sort = 'newest' } = filters;

  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, size, totalElements: 0, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const replaceReview = useCallback((updatedReview) => {
    if (!updatedReview?.reviewUuid) {
      return;
    }
    setReviews((current) => (
      current.map((review) => (
        review.reviewUuid === updatedReview.reviewUuid ? { ...review, ...updatedReview } : review
      ))
    ));
  }, []);

  const load = useCallback(async () => {
    if (typeof request !== 'function') {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await request(buildReviewsQuery({ page, size, serviceUuid, rating, sort }), {
        method: 'GET',
        requestName: 'merchant-customer-reviews-list',
      });
      const data = res?.data ?? res ?? {};
      setSummary(data.summary ?? null);
      setReviews(Array.isArray(data.reviews) ? data.reviews : []);
      setPagination({
        page: data.page ?? 0,
        size: data.size ?? size,
        totalElements: data.totalElements ?? 0,
        totalPages: data.totalPages ?? 0,
      });
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Could not load customer reviews.');
      setReviews([]);
    } finally {
      setIsLoading(false);
    }
  }, [request, page, size, serviceUuid, rating, sort]);

  useEffect(() => {
    load();
  }, [load]);

  return { summary, reviews, pagination, isLoading, error, reload: load, replaceReview };
};

const getErrorMessage = (error, fallback) => (
  error?.data?.message
  || error?.response?.data?.message
  || error?.message
  || fallback
);

export const useReplyToReview = ({ request, onToast, onSuccess } = {}) => {
  const [savingReviewUuid, setSavingReviewUuid] = useState(null);

  const replyToReview = useCallback(async (reviewUuid, reply) => {
    const trimmedReply = String(reply || '').trim();
    if (!reviewUuid) {
      const message = 'Review not found.';
      onToast?.({ message, variant: 'error' });
      throw new Error(message);
    }
    if (!trimmedReply) {
      const message = 'Reply cannot be blank.';
      onToast?.({ message, variant: 'error' });
      throw new Error(message);
    }
    if (trimmedReply.length > REVIEW_REPLY_MAX_LENGTH) {
      const message = `Reply must be ${REVIEW_REPLY_MAX_LENGTH} characters or fewer.`;
      onToast?.({ message, variant: 'error' });
      throw new Error(message);
    }
    if (typeof request !== 'function') {
      const message = 'Store session not found. Please login again.';
      onToast?.({ message, variant: 'error' });
      throw new Error(message);
    }

    setSavingReviewUuid(reviewUuid);
    try {
      const response = await request(PROFILE_STORES_REVIEW_REPLY(reviewUuid), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply: trimmedReply }),
        requestName: `merchant-review-reply-${reviewUuid}`,
      });
      const updatedReview = response?.data ?? response;
      onSuccess?.(updatedReview);
      onToast?.({ message: 'Reply saved.', variant: 'success' });
      return updatedReview;
    } catch (error) {
      onToast?.({
        message: getErrorMessage(error, 'Could not save reply.'),
        variant: 'error',
      });
      throw error;
    } finally {
      setSavingReviewUuid(null);
    }
  }, [request, onToast, onSuccess]);

  const isSavingReview = useCallback(
    (reviewUuid) => Boolean(reviewUuid && savingReviewUuid === reviewUuid),
    [savingReviewUuid]
  );

  return { replyToReview, savingReviewUuid, isSavingReview };
};

export default useStoreReviews;
